import { useAppSelector, useAppDispatch } from 'hooks';
import { useEffect } from 'react';

import { Text, SimpleGrid } from '@chakra-ui/react';

import Card from 'components/card/Card';

import FormInput from 'components/fields/home/FormInput';
import DateInput from 'components/fields/home/DateInput';
import RadioInput from 'components/fields/home/RadioInput';

import getDateFromString from 'helpers/getDateFromString';
import getRadioYesNo from 'helpers/getRadioYesNo';

import {
  changeBrokerQuestionnaire,
  removeMissingMandatoryField,
} from 'reducers/homePersonalFormReducer';

import getDateString from 'helpers/getDateString';

import { SECTIONS } from 'views/home_form/constants';

const BrokerQuestionnaire = () => {
  const dispatch = useAppDispatch();

  const data = useAppSelector(
    (state) => state.homePersonalForm.data?.broker_questionnaire,
  );

  const onDateChange = (date: Date, name: string) => {
    dispatch(changeBrokerQuestionnaire({ name, value: getDateString(date) }));
  };

  const onRadioChange = (value: string, name: string) => {
    dispatch(changeBrokerQuestionnaire({ name, value: value === 'true' }));
  };

  const onInputChange = (event: any) => {
    const { name, value } = event.target;
    dispatch(changeBrokerQuestionnaire({ name, value }));
  };

  useEffect(() => {
    if (!data) return;

    if (!data.has_seen_primary_location) {
      dispatch(removeMissingMandatoryField('Primary location seen date'));
      dispatch(removeMissingMandatoryField('Condition of property'));
    }
  }, [data?.has_seen_primary_location, dispatch]);

  if (!data) return null;

  return (
    <Card mb="20px" id="sectionBrokerQuestionnaire">
      <Text fontWeight="bold" fontSize="xl" mb="20px">
        Broker Questionnaire
      </Text>

      <SimpleGrid columns={4} spacing="20px">
        <RadioInput
          section={SECTIONS.BROKER_QUESTIONNAIRE}
          label="Is this business new to your office?"
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          value={getRadioYesNo(data.has_business_new_to_office)}
          onChange={(value: string) =>
            onRadioChange(value, 'has_business_new_to_office')
          }
          stacked
          required
        />

        <DateInput
          section={SECTIONS.BROKER_QUESTIONNAIRE}
          label="Since what date have you known this applicant?"
          date={getDateFromString(data.date_known_applicant)}
          onChange={(date: Date) => onDateChange(date, 'date_known_applicant')}
        />

        <RadioInput
          section={SECTIONS.BROKER_QUESTIONNAIRE}
          label="Have you bound the risk?"
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          value={getRadioYesNo(data.has_bound_the_risk)}
          onChange={(value: string) =>
            onRadioChange(value, 'has_bound_the_risk')
          }
          stacked
          required
        />

        <RadioInput
          section={SECTIONS.BROKER_QUESTIONNAIRE}
          label="Are there special circumstances?"
          infoText="Are there special circumstances regarding this application which the company should know?"
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          value={getRadioYesNo(data.has_special_circumstances)}
          onChange={(value: string) =>
            onRadioChange(value, 'has_special_circumstances')
          }
          stacked
          required
        />

        <RadioInput
          section={SECTIONS.BROKER_QUESTIONNAIRE}
          label="Have you seen the primary location?"
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          value={getRadioYesNo(data.has_seen_primary_location)}
          onChange={(value: string) =>
            onRadioChange(value, 'has_seen_primary_location')
          }
          stacked
          required
        />

        <DateInput
          section={SECTIONS.BROKER_QUESTIONNAIRE}
          label="Primary location seen date"
          date={getDateFromString(data.primary_location_seen_date)}
          onChange={(date: Date) =>
            onDateChange(date, 'primary_location_seen_date')
          }
          required={data.has_seen_primary_location}
        />

        <FormInput
          section={SECTIONS.BROKER_QUESTIONNAIRE}
          label="Condition of property"
          name="condition_of_property"
          value={data.condition_of_property || ''}
          onChange={onInputChange}
          required={data.has_seen_primary_location}
        />
      </SimpleGrid>
    </Card>
  );
};

export default BrokerQuestionnaire;
